/**
 * GenerateFromDocumentDialog — génération d'un modèle depuis un PDF (9.U.3).
 *
 * Parcours : sélection du PDF → **avertissement explicite + confirmation humaine
 * obligatoire** (le contenu du document sera transmis au fournisseur LLM
 * configuré) → upload (flow existant `initiate → PUT signé → complete`, kind
 * `form_template_source`) → génération → navigation vers l'éditeur du BROUILLON.
 *
 * Sécurité / conformité :
 *   - aucune génération sans clic de confirmation après lecture de l'avertissement ;
 *   - `generation_request_id` généré UNE fois par ouverture du dialogue (ref
 *     stable) → un retry après échec réémet la MÊME clé (rejeu serveur, aucun
 *     doublon) ; une régénération volontaire passe par une nouvelle ouverture ;
 *   - erreurs affichées via le registre bilingue (aucun détail technique) ;
 *   - aucun `console.log` du fichier, du texte ou de la réponse.
 *
 * Réutilise les hooks d'upload existants + `sha256OfFile`/`uploadToBucket` :
 * même mécanisme que `PdfUploadDropzone`, mais ce dernier impose un parent
 * (project/submission/company) et un kind propres au domaine projets — non
 * applicables ici (un modèle n'a pas de dossier parent).
 */
import { useCallback, useEffect, useId, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import { ApiException } from "../../../lib/api";
import { getApiErrorMessage, normalizeLang } from "../../../lib/apiErrorMessage";
import { useCompleteFileUpload, useInitiateFileUpload } from "../../../lib/queries/files";
import { useGenerateFormTemplate } from "../../../lib/queries/formTemplates";
import { beginTenantRequest } from "../../../lib/tenantScope";
import { sha256OfFile, uploadToBucket, UploadToBucketError } from "../../../lib/uploadPdf";
import { formVersionEditorPath } from "../editorRoute";

type Props = {
  open: boolean;
  onClose: () => void;
};

type Phase = "select" | "confirm" | "working" | "error";

/** Taille max côté client — reprend la borne d'upload existante (25 Mo). */
const MAX_SIZE_BYTES = 25 * 1024 * 1024;

export function GenerateFromDocumentDialog({ open, onClose }: Props) {
  const { t, i18n } = useTranslation();
  const lang = normalizeLang(i18n.resolvedLanguage);
  const navigate = useNavigate();
  const titleId = useId();
  const fileInputId = useId();
  const closeBtnRef = useRef<HTMLButtonElement | null>(null);

  const [file, setFile] = useState<File | null>(null);
  const [phase, setPhase] = useState<Phase>("select");
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  // Clé d'idempotence : UNE par ouverture de dialogue. Un retry réémet la même
  // clé → le serveur rejoue au lieu de régénérer/dupliquer.
  const requestIdRef = useRef<string>(crypto.randomUUID());

  const initiate = useInitiateFileUpload();
  const complete = useCompleteFileUpload();
  const generate = useGenerateFormTemplate();
  const busy = phase === "working";

  useEffect(() => {
    if (open) {
      setFile(null);
      setPhase("select");
      setErrorMessage(null);
      requestIdRef.current = crypto.randomUUID();
      queueMicrotask(() => closeBtnRef.current?.focus());
    }
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape" && !busy) {
        e.preventDefault();
        onClose();
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [open, onClose, busy]);

  const onPickFile = (f: File | null) => {
    setErrorMessage(null);
    if (!f) {
      setFile(null);
      setPhase("select");
      return;
    }
    if (f.type !== "application/pdf") {
      setFile(null);
      setErrorMessage(t("form_templates.generate.errors.not_pdf"));
      return;
    }
    if (f.size > MAX_SIZE_BYTES) {
      setFile(null);
      setErrorMessage(t("form_templates.generate.errors.too_large"));
      return;
    }
    setFile(f);
    setPhase("confirm");
  };

  const onConfirm = useCallback(async () => {
    if (!file || busy) return;
    const operation = beginTenantRequest(true);
    setPhase("working");
    setErrorMessage(null);
    try {
      // 1. Upload via le flow existant (kind sans parent, 9.U.3).
      const sha256 = await sha256OfFile(file);
      operation.assertCurrent();
      const initiated = await initiate.mutateAsync({
        kind: "form_template_source",
        file_name: file.name,
        size_bytes: file.size,
        mime_type: "application/pdf",
        sha256,
      });
      operation.assertCurrent();
      await uploadToBucket({
        signal: operation.signal,
        url: initiated.upload.url,
        headers: initiated.upload.headers,
        body: file,
      });
      operation.assertCurrent();
      await complete.mutateAsync({ fileId: initiated.file.id, input: { sha256 } });

      // 2. Génération (brouillon) — clé d'idempotence stable.
      operation.assertCurrent();
      const result = await generate.mutateAsync({
        source_file_id: initiated.file.id,
        generation_request_id: requestIdRef.current,
      });

      // 3. Revue humaine : on ouvre l'éditeur du brouillon généré.
      operation.assertCurrent();
      navigate(formVersionEditorPath(result.template.id, result.draft_version.id));
      onClose();
    } catch (err) {
      setPhase("error");
      // 9.BJ.4 — un échec du téléversement n'est PAS un échec de génération.
      //
      // Le dialogue enchaîne trois étapes ; une seule catch les couvrait toutes,
      // et disait « la génération a échoué » même quand le binaire n'avait
      // jamais quitté le navigateur. C'est ce message qui a fait chercher du
      // côté du fournisseur d'IA pendant que le problème était deux étapes plus
      // tôt — un `PUT` refusé par le stockage.
      setErrorMessage(
        err instanceof UploadToBucketError
          ? t("form_templates.generate.errors.upload_failed")
          : err instanceof ApiException
            ? getApiErrorMessage(err, lang)
            : t("form_templates.generate.errors.generic"),
      );
    } finally {
      operation.finish();
    }
  }, [busy, complete, file, generate, initiate, lang, navigate, onClose, t]);

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby={titleId}
      onClick={() => {
        if (!busy) onClose();
      }}
    >
      <div
        className="w-full max-w-lg rounded-2xl bg-white p-6 shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="flex items-start justify-between gap-4">
          <div>
            <h2 id={titleId} className="text-lg font-semibold text-slate-900">
              {t("form_templates.generate.title")}
            </h2>
            <p className="mt-1 text-sm text-slate-500">{t("form_templates.generate.subtitle")}</p>
          </div>
          <button
            ref={closeBtnRef}
            type="button"
            onClick={onClose}
            disabled={busy}
            aria-label={t("form_templates.generate.close")}
            className="focus-visible:ring-brand-500 rounded-md p-1.5 text-slate-500 hover:bg-slate-100 focus-visible:outline-none focus-visible:ring-2 disabled:opacity-50"
          >
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none" aria-hidden="true">
              <path
                d="M5 5l10 10M15 5L5 15"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
              />
            </svg>
          </button>
        </header>

        <div className="mt-5 space-y-4">
          <div>
            <label htmlFor={fileInputId} className="block text-sm font-medium text-slate-700">
              {t("form_templates.generate.file_label")}
            </label>
            <input
              id={fileInputId}
              type="file"
              accept="application/pdf"
              disabled={busy}
              onChange={(e) => onPickFile(e.target.files?.[0] ?? null)}
              className="focus-visible:ring-brand-500 mt-1 block w-full rounded-lg border border-slate-300 px-3 py-2 text-sm text-slate-900 file:mr-3 file:rounded-md file:border-0 file:bg-slate-100 file:px-3 file:py-1.5 file:text-sm file:font-medium focus-visible:outline-none focus-visible:ring-2 disabled:opacity-50"
            />
            <p className="mt-1 text-xs text-slate-500">{t("form_templates.generate.file_hint")}</p>
          </div>

          {/* Avertissement + confirmation humaine OBLIGATOIRES avant tout appel LLM. */}
          <div
            role="note"
            className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900"
          >
            <p className="font-medium">{t("form_templates.generate.warning_title")}</p>
            <p className="mt-1">{t("form_templates.generate.warning_body")}</p>
          </div>

          {errorMessage && (
            <div
              role="alert"
              className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700"
            >
              {errorMessage}
            </div>
          )}

          {busy && (
            <p role="status" aria-live="polite" className="text-sm text-slate-600">
              {t("form_templates.generate.working")}
            </p>
          )}
        </div>

        <footer className="mt-6 flex items-center justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            disabled={busy}
            className="focus-visible:ring-brand-500 rounded-lg border border-slate-300 bg-white px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 disabled:opacity-50"
          >
            {t("form_templates.generate.cancel")}
          </button>
          <button
            type="button"
            onClick={() => void onConfirm()}
            disabled={!file || busy}
            className="bg-brand-600 hover:bg-brand-700 focus-visible:ring-brand-500 inline-flex touch-manipulation items-center justify-center gap-2 rounded-lg px-4 py-2 text-sm font-semibold text-white shadow-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 disabled:opacity-50"
          >
            {busy
              ? t("form_templates.generate.working_short")
              : t("form_templates.generate.confirm")}
          </button>
        </footer>
      </div>
    </div>
  );
}
